import {
  Group,
  Intersection,
  MeshBasicMaterial,
  Object3D,
  Raycaster,
  Texture,
  Vector3,
} from "three";
import { Font } from "three/examples/jsm/loaders/FontLoader";
import font from "../../../fonts/RoundedMplus1c_Regular.json";
import * as Config from "../config/ViewerConfig";
import { AutoAttenuableLine } from "../object/AutoAttenuableLine";
import { AutoAttenuablePin } from "../object/AutoAttenuablePin";
import { AutoAttenuatableSphere } from "../object/AutoAttenuatableSphere";
import { AutoAttenuatableTextSprite } from "../object/AutoAttenuatableTextSprite";
import { Viewer } from "../viewer";
import {
  IMeasurementHandler,
  IMeasurementVisibleParams,
  MeasurementMode,
  MeasurementState,
  MeasurementType,
} from "./types";
import { getRoundRect } from "./utils";

export class MultiplePointMeasurement implements IMeasurementHandler {
  type: MeasurementType = MeasurementType.MULTIPLE_POINT;
  _id: number;
  _points: Vector3[];
  value: number;

  private _group: Group;

  private _viewer: Viewer;
  private _mode: MeasurementMode;

  private _pins: AutoAttenuablePin[];

  private _lineMaterial: MeshBasicMaterial;
  private _lines: AutoAttenuableLine[];

  private _sphereMaterial: MeshBasicMaterial;
  private _spheres: AutoAttenuatableSphere[];

  private _font: Font;

  private _textSprites: AutoAttenuatableTextSprite[];
  private _textTextureContexts: CanvasRenderingContext2D[];
  private _textTextures: Texture[];

  private _totalSprite: AutoAttenuatableTextSprite;
  private _totalTextureContext: CanvasRenderingContext2D;
  private _totalTexture: Texture;

  private _numValues: number[];
  private _textScale: Config.ITextScale;
  private _isSelected: boolean = false;

  public get id(): number {
    return this._id;
  }

  constructor(viewer: Viewer, textScale: Config.ITextScale) {
    this._viewer = viewer;
    this.value = 0;
    this._points = [];
    this._pins = [];
    this._spheres = [];
    this._lines = [];
    this._textSprites = [];
    this._numValues = [];
    this._textTextures = [];
    this._textTextureContexts = [];

    this._group = new Group();
    this._viewer.getScene(Config.SCENE_TYPE.MEASUREMENT).add(this._group);
    this._mode = MeasurementMode.CREATE;
    this.appearanceState = "default";

    this._lineMaterial = new MeshBasicMaterial({
      color: Config.NORMAL_MEASUREMENT_COLOR,
      depthTest: false,
      transparent: true,
    });

    this._sphereMaterial = new MeshBasicMaterial({
      color: Config.NORMAL_MEASUREMENT_COLOR,
    });

    this._font = new Font(font);

    const textCanvas = document.createElement("canvas");
    textCanvas.width = 1000;
    textCanvas.height = 500;
    this._totalTextureContext = textCanvas.getContext("2d");
    this._totalTexture = new Texture(textCanvas);
    this._totalTexture.needsUpdate = true;
    this._totalSprite = new AutoAttenuatableTextSprite(
      this._totalTexture,
      textScale
    );
    this._textScale = textScale;
    this._totalSprite.visible = false;
    this._group.add(this._totalSprite);
  }
  appearanceState: MeasurementState;
  hitText(ray: Raycaster): Intersection[] {
    const objects = [this._totalSprite, ...this._lines];
    objects.forEach((obj) => {
      obj.geometry.computeBoundingBox();
      obj.geometry.computeBoundingSphere();
    });

    return ray.intersectObjects(objects, true);
  }

  private updateTextContext2d(state: MeasurementState, index: number) {
    const canvasWidth = this._textTextureContexts[index].canvas.width;
    const canvasHeight = this._textTextureContexts[index].canvas.height;
    this._textSprites[index].geometry.computeBoundingBox();

    this._textTextureContexts[index].clearRect(0, 0, canvasWidth, canvasHeight);

    const borderThickness = 4;
    const fontsize = 44;
    const fontface = "Arial";
    // const text = `${this.value.toFixed(3)} m`;
    const text = `${this._numValues[index].toFixed(3)} m`;
    const tab1Width = 16;
    const edgeConst = 1.05;

    this._textTextureContexts[index].font = fontsize + "px " + fontface;

    const textHeight = fontsize;

    const textWidth = this._textTextureContexts[index].measureText(text).width;

    this._textTextureContexts[index].lineWidth = borderThickness;
    this._textTextureContexts[index].fillStyle = {
      default: Config.DEFAULT_MEASUREMENT_TEXT_BGCOLOR,
      selected: Config.SELECTED_MEASUREMENT_TEXT_BGCOLOR,
      hovered: Config.HOVERED_MEASUREMENT_TEXT_BGCOLOR,
    }[state];
    this._textTextureContexts[index].strokeStyle = "rgba(0,0,0,1.0)"; //
    const roundedRectWidth = tab1Width + textWidth + borderThickness * 2;
    const roundedRectHeight = textHeight + borderThickness * 2;
    getRoundRect(
      this._textTextureContexts[index],
      borderThickness / 2,
      borderThickness / 2,
      roundedRectWidth,
      roundedRectHeight,
      6
    );

    this._textTextureContexts[index].fillStyle = "rgba(255,255,255,1.0)"; //text color
    this._textTextureContexts[index].strokeStyle = "rgba(255,255,255,1.0)"; //text color

    this._textTextureContexts[index].fillText(text, tab1Width, textHeight);

    // テキストテクスチャが間違いなく収まる幅へ定数で調整
    const newCanvasWidth = Math.ceil(roundedRectWidth * edgeConst);
    const newCanvasHeight = Math.ceil(roundedRectHeight * edgeConst);

    const geom = this._textSprites[index].geometry;
    const uvs = geom.attributes["uv"];
    uvs.setXY(0, 0, 1 - newCanvasHeight / canvasHeight);
    uvs.setXY(
      1,
      newCanvasWidth / canvasWidth,
      1 - newCanvasHeight / canvasHeight
    );
    uvs.setXY(2, newCanvasWidth / canvasWidth, 1);
    uvs.setXY(3, 0, 1);
    uvs.needsUpdate = true;
    this._textSprites[index].aspect = newCanvasHeight / newCanvasWidth;

    this._textTextures[index].needsUpdate = true;
  }

  private updateTotalContext2d(state: MeasurementState) {
    const canvasWidth = this._totalTextureContext.canvas.width;
    const canvasHeight = this._totalTextureContext.canvas.height;
    this._totalSprite.geometry.computeBoundingBox();

    this._totalTextureContext.clearRect(0, 0, canvasWidth, canvasHeight);

    const borderThickness = 4;
    const fontsize = 44;
    const fontface = "Arial";
    const text = `Σ：${this.value.toFixed(3)} m`;
    const tab1Width = 16;
    const edgeConst = 1.05;

    this._totalTextureContext.font = fontsize + "px " + fontface;

    const textHeight = fontsize;

    const textWidth = this._totalTextureContext.measureText(text).width;

    this._totalTextureContext.lineWidth = borderThickness;
    this._totalTextureContext.fillStyle = {
      default: Config.DEFAULT_MEASUREMENT_TEXT_BGCOLOR,
      selected: Config.SELECTED_MEASUREMENT_TEXT_BGCOLOR,
      hovered: Config.HOVERED_MEASUREMENT_TEXT_BGCOLOR,
    }[state];

    this._totalTextureContext.strokeStyle = "rgba(0,0,0,1.0)"; //
    const roundedRectWidth = tab1Width + textWidth + borderThickness * 2;
    const roundedRectHeight = textHeight + borderThickness * 2;
    getRoundRect(
      this._totalTextureContext,
      borderThickness / 2,
      borderThickness / 2,
      roundedRectWidth,
      roundedRectHeight,
      6
    );

    this._totalTextureContext.fillStyle = "rgba(255,255,255,1.0)"; //text color
    this._totalTextureContext.strokeStyle = "rgba(255,255,255,1.0)"; //text color

    this._totalTextureContext.fillText(text, tab1Width, textHeight);

    // テキストテクスチャが間違いなく収まる幅へ定数で調整
    const newCanvasWidth = Math.ceil(roundedRectWidth * edgeConst);
    const newCanvasHeight = Math.ceil(roundedRectHeight * edgeConst);

    const geom = this._totalSprite.geometry;
    const uvs = geom.attributes["uv"];
    uvs.setXY(0, 0, 1 - newCanvasHeight / canvasHeight);
    uvs.setXY(
      1,
      newCanvasWidth / canvasWidth,
      1 - newCanvasHeight / canvasHeight
    );
    uvs.setXY(2, newCanvasWidth / canvasWidth, 1);
    uvs.setXY(3, 0, 1);
    uvs.needsUpdate = true;
    this._totalSprite.aspect = newCanvasHeight / newCanvasWidth + 0.1;

    this._totalTexture.needsUpdate = true;
  }

  public setPoints(points: Vector3[]) {
    this._points = points.map((p) => p.clone());
    this._id =
      this._id === undefined ? this._viewer.generateMeasurementId() : this._id;
    // this._mode = MeasurementMode.INACTIVE;

    this.disposeSpheres();
    this.disposePins();
    this.disposeLine();
    this.disposeTextSprite();

    for (let i = 0; i < this._points.length; ++i) {
      const sphere = new AutoAttenuatableSphere(
        this._sphereMaterial,
        Config.MEASUREMENT_SPHERE_SIZE
      );
      sphere.position.copy(points[i]);
      this._group.add(sphere);
      this._spheres.push(sphere);

      const pin = new AutoAttenuablePin(`${i}`, this._viewer);
      pin.setPosition(this._points[i]);
      this._group.add(pin);
      this._pins.push(pin);

      if (i !== 0) {
        const line = new AutoAttenuableLine(i - 1, this._lineMaterial);
        line.visible = false;
        this._group.add(line);
        this._lines.push(line);

        // EDIT時: 各点間距離描画
        if (this._mode === MeasurementMode.EDIT) {
          const textCanvas = document.createElement("canvas");
          textCanvas.width = 1000;
          textCanvas.height = 500;
          this._textTextureContexts[i - 1] = textCanvas.getContext("2d");
          this._textTextures[i - 1] = new Texture(textCanvas);
          this._textTextures[i - 1].needsUpdate = true;
          const textSprite = new AutoAttenuatableTextSprite(
            this._textTextures[i - 1],
            this._textScale
          );
          textSprite.visible = false;
          this._group.add(textSprite);
          this._textSprites.push(textSprite);
        }

        let value = Math.sqrt(
          Math.pow(this._points[i].x - this._points[i - 1].x, 2) +
            Math.pow(this._points[i].y - this._points[i - 1].y, 2) +
            Math.pow(this._points[i].z - this._points[i - 1].z, 2)
        );
        this._numValues.push(value);
      }
    }

    this.calculateDistanceTotal();
    this.render();
  }

  private calculateDistanceTotal() {
    this.value = 0;
    for (let i = 0; i < this._points.length; ++i) {
      if (i !== 0) {
        this.value += this._points[i].distanceTo(this._points[i - 1]);
      }
    }
  }

  private calculateDistance() {
    for (let i = 0; i < this._points.length; ++i) {
      if (i !== 0) {
        this._numValues[i - 1] = Math.sqrt(
          Math.pow(this._points[i].x - this._points[i - 1].x, 2) +
            Math.pow(this._points[i].y - this._points[i - 1].y, 2) +
            Math.pow(this._points[i].z - this._points[i - 1].z, 2)
        );
      }
    }
  }

  private disposeSpheres() {
    for (let sphere of this._spheres) {
      // this._group.remove(sphere);
      sphere.dispose();
      sphere = undefined;
    }
    this._spheres.length = 0;
  }

  private disposePins() {
    for (let pin of this._pins) {
      this._group.remove(pin);
      pin.geometry.dispose();
      pin.geometry = undefined;
      pin.material.dispose();
      pin.material = undefined;
      pin = undefined;
    }
    this._pins.length = 0;
  }

  private disposeLine() {
    for (let line of this._lines) {
      this._group.remove(line);
      line.dispose();
      line = undefined;
    }
    this._lines = [];
  }

  private disposeTextSprite() {
    for (var textSprite of this._textSprites) {
      this._group.remove(textSprite);
      textSprite.dispose();
      textSprite = undefined;
    }
    this._textSprites = [];
  }

  public dispose() {
    this._points.length = 0;

    this.disposePins();
    this.disposeSpheres();
    this.disposeLine();
    this.disposeTextSprite();

    this._lineMaterial.dispose();
    this._lineMaterial = undefined;

    this._sphereMaterial.dispose();
    this._sphereMaterial = undefined;

    this._group.remove(this._totalSprite);
    this._totalSprite.dispose();
    this._totalSprite = undefined;

    this._viewer.updateRenderer();
  }

  public render() {
    for (let i = 0; i < this._points.length; i++) {
      if (i !== 0) {
        this._lines[i - 1].updatePoints(this._points[i - 1], this._points[i]);
        this._lines[i - 1].visible = true;
      }
    }

    for (const pin of this._pins) {
      pin.visible = false;
    }

    for (const sphere of this._spheres) {
      sphere.visible = false;
    }

    switch (this._mode) {
      case MeasurementMode.EDIT:
        this._lineMaterial.color.setHex(Config.EDIT_MEASUREMENT_COLOR);
        this._points.forEach((val, i) => this._pins[i].setPosition(val));
        for (const pin of this._pins) {
          pin.visible = true;
        }
        break;
      default:
        this._lineMaterial.color.setHex(Config.NORMAL_MEASUREMENT_COLOR);
        this._points.forEach((val, i) => this._spheres[i].position.copy(val));
        for (const sphere of this._spheres) {
          sphere.visible = true;
        }
    }

    if (this._points.length >= 2) {
      // EDIT時: 各点間距離描画
      if (this._mode === MeasurementMode.EDIT) {
        for (let i = 0; i < this._textSprites.length; i++) {
          this._textTextures[i].needsUpdate = true;
          this.updateTextContext2d(this.appearanceState, i);
          const textPosition = new Vector3(0, 0, 0);
          textPosition.x = (this._points[i].x + this._points[i + 1].x) / 2;
          textPosition.y = (this._points[i].y + this._points[i + 1].y) / 2;
          textPosition.z = (this._points[i].z + this._points[i + 1].z) / 2;
          this._textSprites[i].setPosition(textPosition);
          this._textSprites[i].visible = true;
        }
      } else {
        this.disposeTextSprite();
      }

      this.updateTotalContext2d(this.appearanceState);
      this._totalSprite.setPosition(this._points[this._points.length - 1]);
      this._totalSprite.visible = true;
    }

    this._viewer.updateRenderer();
  }

  public switchMode(mode: MeasurementMode) {
    this._mode = mode;
    this.render();
  }

  public onPositionUpdate(id: number, position: Vector3) {
    this._points[id].copy(position);
    this.calculateDistanceTotal();
    this.calculateDistance();
    this.render();
  }

  public onUpdateComplete(id: number, position: Vector3) {
    this._viewer.setPointsToMeasurement(this._id, this._points);
  }

  public getPickableObjects(): Object3D[] {
    return this._pins;
  }

  public setVisibility(params: IMeasurementVisibleParams) {
    if (params.text !== undefined) {
      for (let textSprite of this._textSprites) {
        textSprite.visible = params.text;
      }
    }
    if (params.measurement !== undefined) {
      this._group.visible = params.measurement;
    }
    this._viewer.updateRenderer();
  }

  public getVisibility(): boolean {
    return this._group.visible;
  }

  public changeAppearanceState(state: MeasurementState): void {
    this.appearanceState = state;
    for (const sphere of this._spheres) {
      sphere.setMaterialByState(state);
      sphere.setGeometryByState(state);
    }
    for (const line of this._lines) {
      line.setMaterialByState(state);
      line.setGeometryByState(state);
    }

    for (let i = 0; i < this._textSprites.length; i++) {
      this.updateTextContext2d(state, i);
      this._textSprites[i].changeRenderOrderByState(state);
    }
    this.updateTotalContext2d(state);
  }
}
